import { isPrintEnabledForDraft } from '@/feature/feature.ts';
import FormField from '@ingka/form-field';
import Select, { Option } from '@ingka/select';
import { translate } from '@repo/phrase';
import type { AssetDataType } from '@repo/types/asset';
import { isDraft } from '@repo/types/draft';

import type { SupportedLocaleOption } from './share-locale-select.utils';
import { isSelectedLocaleValid } from './share-locale-select.utils';

import styles from './styles.module.scss';

interface Props {
    divider?: boolean | undefined;
    data: AssetDataType;
    locale?: string | undefined;
    setLocale: (locale?: string) => void;
    shouldValidate: boolean;
    supportedLocales: SupportedLocaleOption[];
}

const ShareLocaleSelectOption = ({ supportedLocale }: { supportedLocale: SupportedLocaleOption }) => {
    let name = supportedLocale.name;

    if (supportedLocale.isCustomerPreferred) {
        name = `${name} (${translate('share_locale_select_customer_preferred')})`;
    } else if (supportedLocale.isStorePreferred) {
        name = `${name} (${translate('share_locale_select_store_preferred')})`;
    }

    return <Option value={supportedLocale.id} name={name} />;
};

export const ShareLocaleSelect = ({ locale, setLocale, data, shouldValidate, supportedLocales, divider }: Props) => {
    if (supportedLocales.length < 2) {
        return null;
    }

    if (isDraft(data) && !isPrintEnabledForDraft()) {
        return null;
    }

    return (
        <>
            {divider && <hr className={styles.divider} />}
            <div className={styles.form_group} data-testid="share-locale-selection">
                <FormField
                    className={String(styles.form_field)}
                    shouldValidate={shouldValidate}
                    valid={isSelectedLocaleValid(supportedLocales, locale)}
                    validation={{
                        msg: translate('form_field_msg_required_field'),
                        id: 'error',
                        type: 'error',
                    }}
                >
                    <Select
                        id="shareLocaleSelect"
                        data-testid="share-locale-select"
                        value={locale ?? ''}
                        hintText={null}
                        describedById={translate('share_locale_select')}
                        label={translate('share_locale_select')}
                        aria-label={translate('share_locale_select')}
                        className={String(styles.form_select)}
                        onChange={(e) => {
                            setLocale(e.target.value);
                        }}
                    >
                        {supportedLocales.length > 0 &&
                            supportedLocales.map((supportedLocale) => (
                                <ShareLocaleSelectOption
                                    supportedLocale={supportedLocale}
                                    key={`share-locale-option-${supportedLocale.id}`}
                                />
                            ))}
                    </Select>
                </FormField>
            </div>
        </>
    );
};
